import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { clientAPI, staffAPI } from '../services/api';
import PageHeader from './PageHeader';
import DeleteIconButton from './DeleteIconButton';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { useDebouncedSearch } from '../hooks/useDebouncedSearch';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { hasPermission, getEffectiveRoleCode } from '../utils/permissions';
import api from '../services/api';
import { SIMULATE_CHANGED_EVENT } from '../services/simulate';
import './ClientList.css';

const PAGE_SIZE = 25;

/** Same display name can exist on multiple staff rows (legacy / sync); disambiguate filter options. */
function assignedScFilterOptionLabel(staffList, s) {
  const base =
    [s.last_name, s.first_name].filter(Boolean).join(', ') ||
    [s.first_name, s.last_name].filter(Boolean).join(' ') ||
    String(s.id);
  const norm = (v) => String(v || '').trim().toLowerCase();
  const key = `${norm(s.last_name)}|${norm(s.first_name)}`;
  const sameNameCount = staffList.filter(
    (x) => `${norm(x.last_name)}|${norm(x.first_name)}` === key
  ).length;
  return sameNameCount > 1 ? `${base} · staff #${s.id}` : base;
}

function ClientList({ unassignedOnly = false }) {
  const navigate = useNavigate();
  const { tenantPath } = useTenantFromUrl();
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchParams] = useSearchParams();
  const [clientSearch, setClientSearch] = useState(searchParams.get('q') || '');
  const [eiSearch, setEiSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('open');
  /** '' = all assignees; 'unassigned' = waiting assignment filter; else staff id string */
  const [assignedScFilter, setAssignedScFilter] = useState('');
  const [myStaffId, setMyStaffId] = useState(null);
  const scDefaultAppliedRef = useRef(false);
  const [staff, setStaff] = useState([]);
  const [filterStaffList, setFilterStaffList] = useState([]);
  /** Debounced for SC filter options query (same as list search) */
  const [debouncedClientSearch, setDebouncedClientSearch] = useState('');
  const [debouncedEiSearch, setDebouncedEiSearch] = useState('');
  const [loadingScFilterOptions, setLoadingScFilterOptions] = useState(false);
  const [assigningId, setAssigningId] = useState(null);
  const [assignFeedback, setAssignFeedback] = useState(null);
  const alertShownRef = useRef(false);
  const sentinelRef = useRef(null);
  /** Ignore out-of-order API responses when filters change (prevents wrong rows replacing a filtered list). */
  const listFetchGenRef = useRef(0);

  const canDeleteClient = hasPermission('clients.delete');
  const canAssignStaff = hasPermission('clients.assign_staff');
  const inlineAssign = unassignedOnly && canAssignStaff;
  /** Managers (and SCs with view_all) can filter My Clients by assigned SC */
  const canFilterAssignedSc =
    !unassignedOnly &&
    (hasPermission('clients.view_all') || getEffectiveRoleCode() !== 'SERVICE_COORDINATOR');
  /** SC without org-wide client view: My Clients is own caseload only (same as backend onlyOwnCaseload). */
  const onlyMyCaseloadList =
    !unassignedOnly &&
    getEffectiveRoleCode() === 'SERVICE_COORDINATOR' &&
    !hasPermission('clients.view_all');
  const [showHiddenFromCaseload, setShowHiddenFromCaseload] = useState(false);
  /** Intake Only: show only clients whose current referral has no recorded Initial IFSP date. */
  const [intakeOnly, setIntakeOnly] = useState(false);

  const loadClients = useCallback(async (pageNum = 1, append = false) => {
    const gen = ++listFetchGenRef.current;
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);
      alertShownRef.current = false;
      const params = { page: pageNum, pageSize };
      if (clientSearch.trim()) params.q = clientSearch.trim();
      if (eiSearch.trim()) params.ei = eiSearch.trim();
      if (unassignedOnly) params.assigned = 'unassigned';
      if (canFilterAssignedSc) {
        if (assignedScFilter === 'unassigned') params.assigned = 'unassigned';
        else if (assignedScFilter) params.assigned_staff_id = assignedScFilter;
      }
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (onlyMyCaseloadList && showHiddenFromCaseload) {
        params.include_hidden_from_caseload = true;
      }
      if (intakeOnly) params.intakeOnly = true;
      const response = await clientAPI.getAll(params);
      if (gen !== listFetchGenRef.current) return;
      const data = response.data.data ?? [];
      const newTotal = response.data.total ?? 0;
      if (append) setClients((prev) => [...prev, ...data]);
      else setClients(data);
      setTotal(newTotal);
      setPage(pageNum);
    } catch (error) {
      if (gen !== listFetchGenRef.current) return;
      console.error('Error loading clients:', error);
      if (!alertShownRef.current) {
        alertShownRef.current = true;
        const status = error.response?.status;
        if (status === 401) {
          alert('Please log in again. You may have switched to a different backend (e.g. Azure).');
          navigate('/login', { replace: true });
        } else {
          const msg = error.response?.data?.error || error.message || 'Unknown error';
          const details = error.response?.data?.details;
          const detail = status ? ` (${status})` : '';
          const fullMsg = details ? `${msg} ${details}` : msg;
          alert(`Failed to load clients${detail}. ${fullMsg}`);
        }
      }
    } finally {
      if (gen === listFetchGenRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [
    clientSearch,
    eiSearch,
    statusFilter,
    unassignedOnly,
    assignedScFilter,
    canFilterAssignedSc,
    onlyMyCaseloadList,
    showHiddenFromCaseload,
    intakeOnly,
    navigate,
  ]);

  useDebouncedSearch(`${clientSearch}|${eiSearch}`, () => loadClients(1));

  useEffect(() => {
    const id = setTimeout(() => setDebouncedClientSearch(clientSearch), 350);
    return () => clearTimeout(id);
  }, [clientSearch]);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedEiSearch(eiSearch), 350);
    return () => clearTimeout(id);
  }, [eiSearch]);

  useEffect(() => {
    loadClients(1);
  }, [unassignedOnly, statusFilter, assignedScFilter, showHiddenFromCaseload, intakeOnly, loadClients]);

  // Fetch effective staff_id via whoami (respects ViewAs simulation, unlike localStorage).
  const fetchMyStaffId = useCallback(async () => {
    try {
      const res = await api.get('/whoami');
      const sid = res.data?.user?.staff_id ?? null;
      setMyStaffId(sid != null ? String(sid) : null);
    } catch {
      setMyStaffId(null);
    }
  }, []);

  useEffect(() => {
    fetchMyStaffId();
  }, [fetchMyStaffId]);

  useEffect(() => {
    const handler = () => {
      scDefaultAppliedRef.current = false;
      fetchMyStaffId();
    };
    window.addEventListener(SIMULATE_CHANGED_EVENT, handler);
    return () => window.removeEventListener(SIMULATE_CHANGED_EVENT, handler);
  }, [fetchMyStaffId]);

  // Default assigned SC filter to self for wide-permission users (trainers, managers, admins).
  useEffect(() => {
    if (!canFilterAssignedSc || !myStaffId) return;
    if (scDefaultAppliedRef.current) return;
    scDefaultAppliedRef.current = true;
    setAssignedScFilter(myStaffId);
  }, [canFilterAssignedSc, myStaffId]);

  // Load active staff for inline assign dropdown
  useEffect(() => {
    if (!inlineAssign) return;
    staffAPI.getAll({ active: 'true' })
      .then((res) => setStaff(res.data?.data ?? res.data ?? []))
      .catch(() => setStaff([]));
  }, [inlineAssign]);

  // Distinct SCs who have ≥1 client matching current status + name/EI search (not all tenant staff)
  useEffect(() => {
    if (!canFilterAssignedSc) {
      setFilterStaffList([]);
      setLoadingScFilterOptions(false);
      return;
    }
    let cancelled = false;
    setLoadingScFilterOptions(true);
    const params = {
      status: statusFilter === 'all' ? 'all' : statusFilter,
    };
    if (debouncedClientSearch.trim()) params.q = debouncedClientSearch.trim();
    if (debouncedEiSearch.trim()) params.ei = debouncedEiSearch.trim();
    clientAPI
      .getAssignedStaffFilterOptions(params)
      .then((res) => {
        if (cancelled) return;
        const rows = res.data?.data ?? [];
        setFilterStaffList(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setFilterStaffList([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingScFilterOptions(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canFilterAssignedSc, statusFilter, debouncedClientSearch, debouncedEiSearch]);

  // Clear SC filter if that staff no longer appears in the narrowed list (e.g. status changed)
  useEffect(() => {
    if (!assignedScFilter || assignedScFilter === 'unassigned') return;
    if (loadingScFilterOptions) return;
    const ok = filterStaffList.some((s) => String(s.id) === assignedScFilter);
    if (!ok) setAssignedScFilter('');
  }, [filterStaffList, assignedScFilter, loadingScFilterOptions]);

  // Clear assign success message after 6s
  useEffect(() => {
    if (!assignFeedback) return;
    const t = setTimeout(() => setAssignFeedback(null), 8000);
    return () => clearTimeout(t);
  }, [assignFeedback]);

  const handleAssignStaff = async (client, newStaffId) => {
    const value = newStaffId === '' || newStaffId == null ? null : newStaffId;
    setAssigningId(client.id);
    setAssignFeedback(null);
    try {
      const response = await clientAPI.update(client.id, { assigned_staff_id: value });
      const postAssign = response.data?.postAssign;
      const staffName = value
        ? staff.find((s) => s.id === value)
          ? `${staff.find((s) => s.id === value).first_name} ${staff.find((s) => s.id === value).last_name}`.trim()
          : 'assignee'
        : null;
      setClients((prev) => prev.filter((c) => c.id !== client.id));
      setTotal((t) => Math.max(0, t - 1));
      if (value && staffName) {
        setAssignFeedback({
          clientName: [client.first_name, client.last_name].filter(Boolean).join(' ') || 'Client',
          staffName,
          notificationSent: postAssign?.notificationSent ?? false,
          documentsGenerated: postAssign?.documentsGenerated ?? 0,
          documentsFailed: postAssign?.documentsFailed ?? 0,
          documentsGeneratedNames: postAssign?.documentsGeneratedNames ?? [],
        });
      }
    } catch (err) {
      console.error('Failed to assign client:', err);
      alert(err.response?.data?.error || 'Failed to update assignment');
    } finally {
      setAssigningId(null);
    }
  };

  const hasMore = clients.length < total && total > 0;
  const loadMore = () => {
    if (!loading && !loadingMore && hasMore) loadClients(page + 1, true);
  };
  useInfiniteScroll(sentinelRef, loadMore, hasMore, loading || loadingMore);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this client?')) {
      return;
    }

    try {
      await clientAPI.delete(id);
      setClients((prev) => prev.filter((c) => c.id !== id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (error) {
      console.error('Error deleting client:', error);
      alert('Failed to delete client');
    }
  };

  const showAssignedScColumn = !unassignedOnly;
  const colCount =
    4 + (showAssignedScColumn || inlineAssign ? 1 : 0) + (canDeleteClient ? 1 : 0);

  const formatAssignedScName = (client) => {
    const fn = client.assigned_staff_first_name;
    const ln = client.assigned_staff_last_name;
    if (ln || fn) return [ln, fn].filter(Boolean).join(', ') || '—';
    if (client.assigned_staff_id) return '—';
    return 'Unassigned';
  };

  // A client is a coverage client if we're viewing our own caseload and the primary SC isn't us
  const isCoverageClient = (client) =>
    myStaffId &&
    (onlyMyCaseloadList || assignedScFilter === myStaffId) &&
    client.assigned_staff_id != null &&
    String(client.assigned_staff_id) !== String(myStaffId);

  return (
    <div className="client-list">
      <div className="header">
        <PageHeader title={unassignedOnly ? 'Clients Waiting Assignment' : 'Clients'} />
      </div>

      <div className="client-list-pagination-info">
        {loading && clients.length === 0
          ? 'Loading...'
          : total === 0
            ? (unassignedOnly ? 'No clients waiting assignment' : 'No clients found')
            : total === 1
              ? (unassignedOnly ? '1 client waiting assignment' : '1 client found')
              : (unassignedOnly ? `${total} clients waiting assignment` : `${total} clients found`)}
        <label className="client-list-show-hidden-caseload" style={{ marginLeft: '0.75rem' }}>
          <input
            type="checkbox"
            checked={intakeOnly}
            onChange={(e) => setIntakeOnly(e.target.checked)}
          />
          Intake Only
        </label>
        {onlyMyCaseloadList && (
          <label className="client-list-show-hidden-caseload">
            <input
              type="checkbox"
              checked={showHiddenFromCaseload}
              onChange={(e) => setShowHiddenFromCaseload(e.target.checked)}
            />
            Show hidden
          </label>
        )}
      </div>

      {assignFeedback && (
        <div className="client-list-assign-feedback" role="status">
          <strong>{assignFeedback.clientName}</strong> assigned to <strong>{assignFeedback.staffName}</strong>.
          {assignFeedback.notificationSent ? ' Notification sent to their inbox.' : ''}
          {assignFeedback.documentsGenerated > 0 && (
            <> {assignFeedback.documentsGeneratedNames?.length > 0
              ? `${assignFeedback.documentsGeneratedNames.join(', ')} have been generated and stored in the client folder.`
              : `${assignFeedback.documentsGenerated} document(s) have been generated and stored in the client folder.`}</>
          )}
          {assignFeedback.documentsFailed > 0 && (
            <> ({assignFeedback.documentsFailed} document(s) failed to generate.)</>
          )}
        </div>
      )}

      <div className="client-list-table-wrap list-table-wrap">
        <table className="client-list-table list-table">
          <thead>
            <tr className="list-header-labels">
              <th className="col-client">Client</th>
              <th className="col-ei">EI</th>
              <th className="col-status">Referral Status</th>
              <th className="col-comment">Communication note</th>
              {showAssignedScColumn && <th className="col-assigned-sc">Assigned SC</th>}
              {inlineAssign && <th className="col-assign-sc">Assigned SC</th>}
              {canDeleteClient && <th className="col-delete">Delete</th>}
            </tr>
            <tr className="list-filter-row client-list-filter-row">
              <th className="col-client">
                <input
                  type="text"
                  className="client-list-filter-input"
                  placeholder="Search client..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  aria-label="Filter by client name"
                />
              </th>
              <th className="col-ei">
                <input
                  type="text"
                  className="client-list-filter-input"
                  placeholder="Search EI..."
                  value={eiSearch}
                  onChange={(e) => setEiSearch(e.target.value)}
                  aria-label="Filter by EI number"
                />
              </th>
              <th className="col-status">
                <select
                  className="list-header-filter-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  aria-label="Filter by referral status"
                >
                  <option value="all">All</option>
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                  <option disabled>──</option>
                  <option value="incomplete">Incomplete</option>
                  <option value="waiting">Waiting</option>
                  <option value="active">Active</option>
                  <option value="closing_out">Closing Out</option>
                  <option value="transferring_out">Transferring Out</option>
                  <option value="transferring_in">Transferring In</option>
                </select>
              </th>
              <th className="col-comment client-list-comment-filter" aria-label="Communication note (not filterable)" />
              {showAssignedScColumn && (
                <th className="col-assigned-sc">
                  {canFilterAssignedSc ? (
                    <select
                      className="list-header-filter-select client-list-sc-filter"
                      value={assignedScFilter}
                      onChange={(e) => setAssignedScFilter(e.target.value)}
                      disabled={loadingScFilterOptions}
                      title="Service coordinators who have at least one client matching Status and Client/EI filters above"
                      aria-label="Filter by assigned service coordinator"
                    >
                      <option value="">
                        {loadingScFilterOptions ? 'Loading SCs…' : 'All SCs'}
                      </option>
                      <option value="unassigned">Unassigned</option>
                      {filterStaffList.map((s) => (
                        <option key={s.id} value={String(s.id)}>
                          {assignedScFilterOptionLabel(filterStaffList, s)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="client-list-sc-filter-placeholder" aria-hidden="true">
                      —
                    </span>
                  )}
                </th>
              )}
              {inlineAssign && <th className="col-assign-sc" />}
              {canDeleteClient && <th className="col-delete" />}
            </tr>
          </thead>
          <tbody>
            {loading && clients.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="no-data loading-cell">Loading clients...</td>
              </tr>
            ) : clients.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="no-data">No clients found</td>
              </tr>
            ) : (
              clients.map((client) => {
                const clientName = [client.last_name, client.first_name].filter(Boolean).join(', ') || '—';
                const isAssigning = assigningId === client.id;
                const REFERRAL_STATUS_LABELS = { incomplete: 'Incomplete', waiting: 'Waiting', active: 'Active', closing_out: 'Closing Out', transferring_out: 'Transferring Out', transferring_in: 'Transferring In', closed: 'Closed' };
                const statusDisplay = client.current_referral_status
                  ? (REFERRAL_STATUS_LABELS[client.current_referral_status] || client.current_referral_status)
                  : (client.status || '—');
                const communicationNoteRaw =
                  client.communication_note != null && String(client.communication_note).trim() !== ''
                    ? String(client.communication_note).trim()
                    : '';
                return (
                  <tr
                    key={client.id}
                    onClick={() => navigate(tenantPath(`/client/${client.id}`))}
                    className="clickable-row"
                  >
                    <td className="col-client">
                      {clientName}
                      {isCoverageClient(client) && (
                        <span
                          className="dashboard-coverage-badge"
                          title={`Covering for ${formatAssignedScName(client)}`}
                        >
                          Cov
                        </span>
                      )}
                    </td>
                    <td className="col-ei">{client.ei_number || 'N/A'}</td>
                    <td className="col-status">{statusDisplay}</td>
                    <td
                      className="col-comment client-list-comment-cell"
                      title={communicationNoteRaw || undefined}
                    >
                      <span className="client-list-comment-text">{communicationNoteRaw || '—'}</span>
                    </td>
                    {showAssignedScColumn && (
                      <td
                        className="col-assigned-sc"
                        title={
                          client.assigned_staff_id != null
                            ? `Assigned staff id: ${client.assigned_staff_id}`
                            : undefined
                        }
                      >
                        {formatAssignedScName(client)}
                      </td>
                    )}
                    {inlineAssign && (
                      <td className="col-assign-sc" onClick={(e) => e.stopPropagation()}>
                        <select
                          className="client-list-assign-select"
                          value={client.assigned_staff_id ?? ''}
                          onChange={(e) => handleAssignStaff(client, e.target.value)}
                          disabled={isAssigning}
                          aria-label={`Assign ${clientName}`}
                        >
                          <option value="">Unassigned</option>
                          {staff.map((s) => (
                            <option key={s.id} value={s.id}>
                              {[s.first_name, s.last_name].filter(Boolean).join(' ') || s.id}
                            </option>
                          ))}
                        </select>
                      </td>
                    )}
                    {canDeleteClient && (
                      <td className="col-delete" onClick={(e) => e.stopPropagation()}>
                        <DeleteIconButton
                          className="list-btn-delete"
                          onClick={() => handleDelete(client.id)}
                        />
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {hasMore && <div ref={sentinelRef} className="infinite-scroll-sentinel" aria-hidden="true" />}
      {loadingMore && <div className="infinite-scroll-loading">Loading more...</div>}
    </div>
  );
}

export default ClientList;
